<template>
  <div class="commonLangMenu">
    <button
      v-for="language in activeLanguages"
      :key="language.code"
      :class="`commonLangMenu__button_${language.code}`"
      class="global-main-2 commonLangMenu__button"
      @click="switchLanguage(language.code)"
    >
      <span
        class="commonLangMenu__str"
        :class="{ commonLangMenu__str_current: language.code === $i18n.locale }"
        >{{ language.name }}</span
      >
      <svg
        class="commonLangMenu__svg"
        v-if="language.code === $i18n.locale"
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          class="commonLangMenu__svgPath"
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M9 0C4.02944 0 0 4.02944 0 9C0 13.9706 4.02944 18 9 18C13.9706 18 18 13.9706 18 9C18 4.02944 13.9706 0 9 0ZM13.7747 7.02063C14.0623 6.68517 14.0234 6.18013 13.688 5.89259C13.3525 5.60506 12.8475 5.64391 12.5599 5.97937L8.12213 11.1568L5.39967 8.43431C5.08725 8.12189 4.58072 8.12189 4.2683 8.43431C3.95588 8.74673 3.95588 9.25327 4.2683 9.56569L7.60163 12.899C7.7592 13.0566 7.97535 13.1413 8.19802 13.1327C8.42069 13.1242 8.6297 13.0232 8.77472 12.854L13.7747 7.02063Z"
        />
      </svg>
    </button>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import detectBrowserLanguage from "detect-browser-language";

const LANGUAGES = {
  ru: {
    code: "ru",
    name: "Русский",
  },
  en: {
    code: "en",
    name: "English",
  },
  es: {
    code: "es",
    name: "Español",
  },
  ar: {
    code: "ar",
    name: "العربية",
  },
  hr: {
    code: "hr",
    name: "Hrvatski",
  },
  bg: {
    code: "bg",
    name: "Български",
  },
  hu: {
    code: "hu",
    name: "Magyar",
  },
  it: {
    code: "it",
    name: "Italiano",
  },
  nl: {
    code: "nl",
    name: "Nederlands",
  },
  de: {
    code: "de",
    name: "Deutsch",
  },
  sk: {
    code: "sk",
    name: "Slovenský",
  },
  sl: {
    code: "sl",
    name: "Slovenski",
  },
  el: {
    code: "el",
    name: "Ελληνικά",
  },
  pt: {
    code: "pt",
    name: "Português",
  },
  no: {
    code: "no",
    name: "Norsk",
  },
  hy: {
    code: "hy",
    name: "Հայերեն",
  },
};

export default {
  name: "LangMenu",
  data() {
    return {
      languages: LANGUAGES,
    };
  },
  props: {
    hotelData: null,
  },
  computed: {
    ...mapGetters({
      reservation: "booking/reservation",
    }),
    currentLang() {
      return this.$i18n.locale;
    },
    activeLanguages() {
      const langs = {};
      if (this.reservation) {
        for (const l in this.reservation["lang"]) {
          langs[this.reservation["lang"][l]] =
            LANGUAGES[this.reservation["lang"][l]];
        }
      } else if (this.hotelData) {
        for (const i in this.hotelData.lang) {
          langs[this.hotelData.lang[i]] = LANGUAGES[this.hotelData.lang[i]];
        }
      }

      return langs;
    },
  },
  methods: {
    ...mapMutations({
      SET_LANG: "SET_LANG",
      TOGGLE_LANG_SWITCHER: "TOGGLE_LANG_SWITCHER",
    }),
    async switchLanguage(locale) {
      await this.changeLanguage(locale);
      localStorage.setItem("preferredLang", locale);
    },
    changeLanguage(locale) {
      return new Promise((resolve) => {
        this.$i18n.locale = locale;
        const to = this.$router.resolve({
          params: { locale },
          query: this.$route.query,
        });
        this.SET_LANG(locale);
        this.TOGGLE_LANG_SWITCHER(false);
        this.$router.push(to["fullPath"]);
        resolve();
      });
    },
  },
  async created() {
    const { defaultLanguage } = this.reservation || this.hotelData;
    let browserLocale = detectBrowserLanguage();
    browserLocale = browserLocale.substring(0, 2);
    const langs = this.reservation?.lang || this.hotelData?.lang || ["en"];
    const urlParams = new URLSearchParams(this.$route.query);
    const isEsign = urlParams.get("esign") === "true";
    const langQuery = urlParams.get("lang");
    if (isEsign && langQuery && langs.includes(langQuery)) {
      return await this.changeLanguage(langQuery);
    }
    if (!langs.includes(browserLocale)) {
      browserLocale = langs[0];
    }
    const preferredLang = localStorage.getItem("preferredLang");
    if (preferredLang && this.$i18n.availableLocales.includes(preferredLang)) {
      return await this.changeLanguage(preferredLang);
    }
    if (
      this.$i18n.availableLocales.includes(browserLocale) &&
      browserLocale !== this.$i18n.locale &&
      !preferredLang
    ) {
      return await this.changeLanguage(browserLocale);
    }
    if (
      !this.$i18n.availableLocales.includes(browserLocale) &&
      browserLocale !== this.$i18n.locale &&
      !preferredLang
    ) {
      return await this.changeLanguage(defaultLanguage);
    }
    this.$i18n.locale = this.$route.params.locale || defaultLanguage;
  },
};
</script>

<style lang="scss">
.commonLangMenu {
  padding: 10px 20px;
}

.commonLangMenu__button {
  height: 56px;
  padding: 18px 0;
  width: 100%;
  background-color: transparent;
  border-top: none;
  border-left: none;
  border-right: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: var(--color-text);
  text-align: left;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;

  &:before {
    display: block;
    width: 20px;
    height: 15px;
    content: "";
    margin-right: 10px;
    background-size: cover;
  }

  &_en {
    &:before {
      background-image: url("@/icons/flags/en.svg");
    }
  }

  &_ru {
    &:before {
      background-image: url("@/icons/flags/ru.svg");
    }
  }

  &_es {
    &:before {
      background-image: url("@/icons/flags/es.svg");
    }
  }

  &_ar {
    &:before {
      background-image: url("@/icons/flags/ar.svg");
    }
  }

  &_hr {
    &:before {
      background-image: url("@/icons/flags/hr.svg");
    }
  }

  &_bg {
    &:before {
      background-image: url("@/icons/flags/bg.svg");
    }
  }

  &_de {
    &:before {
      background-image: url("@/icons/flags/de.svg");
    }
  }

  &_hu {
    &:before {
      background-image: url("@/icons/flags/hu.svg");
    }
  }

  &_it {
    &:before {
      background-image: url("@/icons/flags/it.svg");
    }
  }

  &_nl {
    &:before {
      background-image: url("@/icons/flags/nl.svg");
    }
  }

  &_sk {
    &:before {
      background-image: url("@/icons/flags/sk.svg");
    }
  }

  &_sl {
    &:before {
      background-image: url("@/icons/flags/sl.svg");
    }
  }

  &_el {
    &:before {
      background-image: url("@/icons/flags/el.svg");
    }
  }

  &_pt {
    &:before {
      background-image: url("@/icons/flags/pt.svg");
    }
  }

  &_no {
    &:before {
      background-image: url("@/icons/flags/no.svg");
    }
  }

  &_hy {
    &:before {
      background-image: url("@/icons/flags/hy.svg");
    }
  }

  &:last-of-type {
    border-bottom: none;
  }
}

.commonLangMenu__str {
  &_current {
    font-weight: 500;
  }
}

.commonLangMenu__svg {
  margin-left: auto;
  height: 18px;
  width: 18px;
}

.commonLangMenu__svgPath {
  fill: var(--color-text);
}

@include media-breakpoint-up(sm) {
  .commonLangMenu {
    padding: 20px 30px 20px;
  }
}
</style>
